import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, Menu } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { industries } from "@/data/industries";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState } from "react";
import logoHorizontal from "@/assets/logo-horizontal.svg";

const links = [
  { href: "/#services", label: "Services" },
  { href: "/#contact", label: "Contact" },
];

const Navbar = () => {
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const onAnchor = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const href = e.currentTarget.getAttribute("href") || "";
    const id = href.replace("/#", "").replace("#", "");
    
    // If we're not on the homepage, navigate there first
    if (location.pathname !== "/") {
      e.preventDefault();
      navigate(`/#${id}`);
      setMobileMenuOpen(false);
      // After navigation, scroll to element
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } else {
      // Already on homepage, just scroll
      const el = document.getElementById(id);
      if (el) {
        e.preventDefault();
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        setMobileMenuOpen(false);
      }
    }
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <header className="sticky top-0 z-50 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b">
      <nav className="container mx-auto flex items-center justify-between py-3">
        <Link to="/" className="inline-flex items-center">
          <img src={logoHorizontal} alt="AI Montclair" className="h-8 md:h-10 w-auto" />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-6">
          <Link to="/" className="text-base text-muted-foreground hover:text-foreground transition-colors">
            Home
          </Link>
          <Link to="/about" className="text-base text-muted-foreground hover:text-foreground transition-colors">
            About
          </Link>

          <a
            href={links[0].href}
            onClick={onAnchor}
            className="text-base text-muted-foreground hover:text-foreground transition-colors"
          >
            {links[0].label}
          </a>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="inline-flex items-center gap-1">
                Industries <ChevronDown className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="z-50 bg-popover">
              <DropdownMenuItem asChild>
                <Link to="/industries">All Industries</Link>
              </DropdownMenuItem>
              {industries.map((i) => (
                <DropdownMenuItem key={i.slug} asChild>
                  <Link to={`/industries/${i.slug}`}>{i.title}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <a
            href={links[1].href}
            onClick={onAnchor}
            className="text-base text-muted-foreground hover:text-foreground transition-colors"
          >
            {links[1].label}
          </a>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-64">
              <div className="flex flex-col space-y-4 mt-8">
                <Link 
                  to="/" 
                  onClick={closeMobileMenu}
                  className="text-base text-muted-foreground hover:text-foreground transition-colors py-2"
                >
                  Home
                </Link>
                
                <Link 
                  to="/about" 
                  onClick={closeMobileMenu}
                  className="text-base text-muted-foreground hover:text-foreground transition-colors py-2"
                >
                  About
                </Link>

                <a
                  href={links[0].href}
                  onClick={onAnchor}
                  className="text-base text-muted-foreground hover:text-foreground transition-colors py-2"
                >
                  {links[0].label}
                </a>

                <Collapsible open={industriesOpen} onOpenChange={setIndustriesOpen}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between p-2">
                      Industries <ChevronDown className="size-4" />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-4 space-y-2">
                    <Link
                      to="/industries"
                      onClick={closeMobileMenu}
                      className="block text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
                    >
                      All Industries
                    </Link>
                    {industries.map((industry) => (
                      <Link
                        key={industry.slug}
                        to={`/industries/${industry.slug}`}
                        onClick={closeMobileMenu}
                        className="block text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
                      >
                        {industry.title}
                      </Link>
                    ))}
                  </CollapsibleContent>
                </Collapsible>

                <a
                  href={links[1].href}
                  onClick={onAnchor}
                  className="text-base text-muted-foreground hover:text-foreground transition-colors py-2"
                >
                  {links[1].label}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;